"use client"

import { ControlledModal } from "@/components"
import { useLanguagesFilter } from "@/components/Filters/hooks/useLanguagesFilter"
import { SearchInput, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { SetCreativeLanguagesDocument } from "@/gql/graphql"
import { useMutation } from "@apollo/client"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { captureException } from "@sentry/nextjs"
import { FC, useEffect, useMemo, useState } from "react"

const EditLanguagesModalFragment = graphql(`
  fragment EditLanguagesModal on IndividualCreative {
    id
    __typename
    languages {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`)

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  creative: FragmentType<typeof EditLanguagesModalFragment> | null | undefined
}

type Item = { id: string; name: string }

graphql(`
  mutation SetCreativeLanguages($input: SetCreativeLanguagesInput!) {
    setCreativeLanguages(input: $input) {
      errors {
        field
        messages
      }
      creative {
        id
        languages {
          totalCount
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`)

export const EditLanguagesModal: FC<Props> = ({ isOpen, onOpenChange, ...props }) => {
  const [update] = useMutation(SetCreativeLanguagesDocument)
  const creative = useFragment(EditLanguagesModalFragment, props.creative)

  const [search, setSearch] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Item[]>(
    creative?.languages?.edges?.map(edge => ({
      id: edge?.node?.id!,
      name: edge?.node?.name!,
    })) || []
  )

  const languages = useLanguagesFilter()

  const itemsSearched = useMemo(
    () => languages.filter(edge => edge?.node?.name.toLowerCase().includes(search?.toLowerCase())),
    [languages, search]
  )

  const handleOnSelect = (item: { id: string; display: string }) => {
    setSelectedItems([
      ...selectedItems,
      {
        id: item.id,
        name: item.display,
      },
    ])
  }

  useEffect(() => {
    if (selectedItems.length === 0) {
      setSelectedItems(
        creative?.languages?.edges?.map(edge => ({
          id: edge?.node?.id!,
          name: edge?.node?.name!,
        })) || []
      )
    }
  }, [creative])

  const items = useMemo(() => {
    const selectedIds = selectedItems.map(role => role.id)
    return (
      itemsSearched
        ?.map(edge => ({
          id: edge?.node?.id!,
          display: edge?.node?.name!,
        }))
        .filter(item => {
          return !selectedIds.includes(item.id)
        }) || languages
    )
  }, [itemsSearched, languages, selectedItems])

  const onUpdate = async () => {
    setIsLoading(true)
    const response = await update({
      variables: {
        input: {
          id: creative?.id!,
          languages: selectedItems.map(role => role.id),
        },
      },
    })
    setIsLoading(false)
    const errors = response?.data?.setCreativeLanguages?.errors
    if (errors && errors.length > 0) {
      captureException(errors)
      console.error("Error updating roles", errors)
    } else {
      onOpenChange(false)
    }
  }

  const removeRoleFromSelected = (role: Item) => {
    setSelectedItems(selectedItems.filter(selectedRole => selectedRole.id !== role.id))
  }

  return (
    <ControlledModal
      open={isOpen}
      onOpenChange={onOpenChange}
      header={{ title: "Update your languages", variant: "default", fixed: true }}
      body={
        <div className="flex w-full flex-col pt-5 md:pt-6">
          <div className="mb-6 w-full">
            <SearchInput
              type="search"
              placeholder="Search languages"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {Boolean(selectedItems && selectedItems?.length > 0) && (
            <div className="mb-4">
              <div className="mb-2">
                <Text variant="label">Selected languages</Text>
              </div>
              <div className="flex flex-row flex-wrap gap-2">
                {selectedItems.map((role, index) => {
                  return (
                    <div
                      key={role?.id}
                      className="flex cursor-pointer flex-row items-center gap-2 rounded-md bg-fg-1 p-2 hover:bg-fg-2"
                      onClick={() => removeRoleFromSelected(role)}>
                      <Text variant="body1" className="text-bg-1" maxLines={1}>
                        {role?.name}
                      </Text>
                      <XMarkIcon className="size-[18px] stroke-bg-2" />
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          <div className="mb-2">
            <Text variant="label">Results</Text>
          </div>

          {/* Scrollable content area */}
          <div className="flex flex-grow overflow-y-auto pb-5">
            <div className="flex flex-row flex-wrap gap-2 pb-5">
              {items.map((item, index) => (
                <div
                  key={item?.id}
                  className="h-[38px] cursor-pointer items-center rounded-md bg-bg-2 p-2 hover:bg-bg-3"
                  onClick={() => handleOnSelect(item)}>
                  <Text variant="body1" maxLines={1}>
                    {item?.display}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      }
      footer={{
        fixed: true,
        tertiaryButton: {
          children: "Cancel",
          onClick: () => onOpenChange(false),
        },
        primaryButton: {
          children: "Update",
          onClick: onUpdate,
          loading: isLoading,
        },
      }}
    />
  )
}
